import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { ProductInterface } from '../../services/exports/Interfaces';
import useAppMethods from '../../hooks/utility/useAppMethods';
import useConfig from '../../hooks/ui/useConfig';

interface Props {
  items: ProductInterface[];
  maxDiscount: number;
  className?: string;
}

export default function RedeemableItemsCarousel({ items, maxDiscount, className }: Props) {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Cashback:RedeemableItemsCarousel' });
  const { formatCurrency } = useAppMethods();
  const { logo_url } = useConfig();

  if (items.length === 0) {
    return null;
  }

  return (
    <div className={classnames('flex gap-sm overflow-x-auto pb-xs -mx-md px-md scrollbar-hide', className)}>
      {items.map((item) => {
        const afterDiscount = Math.max(item.base_price - maxDiscount, 0);
        const isFree = afterDiscount === 0;
        const imageUrl = item.image_url ?? logo_url;

        return (
          <div key={item.id} className={itemCard}>
            <div className="relative aspect-[4/3] overflow-hidden bg-muted">
              {imageUrl && (
                <img
                  src={imageUrl}
                  alt={item.name}
                  className={classnames(
                    'w-full h-full object-cover group-hover:scale-105 transition-transform duration-500',
                    { '!object-contain bg-brand-card p-sm': !item.image_url },
                  )}
                  loading="lazy"
                />
              )}
              <span
                className={classnames(
                  'absolute top-2xs left-2xs text-[9px] font-bold px-xs py-[2px] rounded-full shadow-sm uppercase tracking-wide',
                  isFree
                    ? 'bg-brand-primary text-brand-button-primary-text'
                    : 'bg-brand-card/95 backdrop-blur-sm text-foreground',
                )}
              >
                {isFree ? t('free') : t('save', { amount: formatCurrency(maxDiscount) })}
              </span>
            </div>
            <div className="p-xs">
              <p className="text-xs text-foreground truncate mb-2xs">{item.name}</p>
              <div className="flex items-baseline gap-2xs">
                <span className="text-[10px] text-muted-foreground line-through">
                  {formatCurrency(item.base_price)}
                </span>
                <span className="text-sm text-brand-primary font-bold">
                  {isFree ? t('free') : formatCurrency(afterDiscount)}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const itemCard =
  'group w-[164px] bg-brand-card rounded-xl border-[1.5px] border-brand-border overflow-hidden shrink-0 hover:shadow-[0_10px_15px_-3px_rgba(0,0,0,0.1),0_4px_6px_-4px_rgba(0,0,0,0.1)] hover:border-brand-primary/30 transition-all duration-300';
